// src/hooks/useImageUpload.js
import { useState, useRef, useEffect } from "react";

export default function useImageUpload() {
	const [image, setImage] = useState(null);
	const fileInputRef = useRef(null);

	// preview URL (auto revoked)
	const [previewUrl, setPreviewUrl] = useState(null);
	useEffect(() => {
		if (!image) {
			setPreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(image);
		setPreviewUrl(url);
		return () => {
			URL.revokeObjectURL(url);
		};
	}, [image]);

	const resizeImage = async (file) => {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = (event) => {
				const img = new Image();
				img.src = event.target.result;
				img.onload = () => {
					const canvas = document.createElement("canvas");
					const ctx = canvas.getContext("2d");
					let width = img.width;
					let height = img.height;
					const maxSize = 800;

					if (width > maxSize || height > maxSize) {
						if (width > height) {
							height *= maxSize / width;
							width = maxSize;
						} else {
							width *= maxSize / height;
							height = maxSize;
						}
					}

					canvas.width = width;
					canvas.height = height;
					ctx.drawImage(img, 0, 0, width, height);
					canvas.toBlob((blob) => resolve(blob), file.type);
				};
			};
		});
	};

	// Returns the selected file (or null). Caller can then call setImage or other actions.
	const handleImageChange = (e) => {
		const file = e?.target?.files?.[0] || null;
		if (!file) {
			setImage(null);
			return null;
		}

		if (file.type === "image/jpeg" || file.type === "image/png") {
			setImage(file);
			return file;
		} else {
			alert("Only JPG and PNG images are allowed.");
			if (e?.target) e.target.value = "";
			return null;
		}
	};

	const resetImage = () => {
		setImage(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	return {
		image,
		previewUrl,
		fileInputRef,
		handleImageChange,
		resizeImage,
		resetImage,
		setImage, // exposed in case caller wants to set programmatically
	};
}
